import { useAutoAnimate } from "@formkit/auto-animate/react";
import {
  useMutationMembershipSubscriptionStartDateAsMember,
  useQueryMemberGetCurrentMembershipSubscriptionAsMember,
} from "@gymflow/api";
import { cn } from "@gymflow/helpers";
import { getDateTimeInGym } from "apps/portal/src/helpers/date";
import { useClubSettings } from "apps/portal/src/providers";
import { ToastContext } from "apps/portal/src/providers/ToastProvider/context";
import useGymflowModels from "apps/portal/src/store";
import { FormikProvider, useFormik } from "formik";
import { DateTime } from "luxon";
import { useContext, useMemo } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useHistory } from "react-router";
import { toFormikValidationSchema } from "zod-formik-adapter";

import { usePortalRoutes } from "../../../hooks";
import { RouteFeature } from "../../../routes";
import { Button, FormLabeledSwitch, Spinner } from "../../atoms";
import { FormSingleDatePicker } from "../../atoms/form/FormSingleDatePicker";
import { HostedPagesBackHeader } from "../components/HostedPagesBackHeader";
import useRedirectIfNotAuthenticated from "../useRedirectIfNotAuthenticated";
import {
  changeStartDateFormSchema,
  INITIAL_VALUES_CHANGE_START_DATE_FORM,
} from "./constants";
import { ChangeStartDateFormValues } from "./types";

export const HostedPagesProfileChangeStartDate = () => {
  const { t } = useTranslation();
  const { isEmbed } = usePortalRoutes();
  const { createSiteOrEmbedLink } = usePortalRoutes();
  const [parent] = useAutoAnimate();
  const { api } = useGymflowModels();
  const {
    propertiesSettings: {
      club: { timezone },
    },
  } = useClubSettings();
  const history = useHistory();
  const { toast } = useContext(ToastContext);

  useRedirectIfNotAuthenticated(
    createSiteOrEmbedLink(
      RouteFeature.SiteBuyMembershipType.replace(":membershipType", "all"),
    ),
  );

  const {
    data: activeMembershipSubscription,
    isLoading: isLoadingActiveMembershipSubscription,
  } = useQueryMemberGetCurrentMembershipSubscriptionAsMember(
    {
      api,
    },
    { refetchOnWindowFocus: true },
  );

  const { mutateAsync: mutateAsyncMembershipSubscriptionStartDate } =
    useMutationMembershipSubscriptionStartDateAsMember({
      api,
    });

  const formik = useFormik<ChangeStartDateFormValues>({
    initialValues: INITIAL_VALUES_CHANGE_START_DATE_FORM,
    validationSchema: toFormikValidationSchema(changeStartDateFormSchema),
    onSubmit: async ({ isStartImmediately, date }) => {
      if (!activeMembershipSubscription?.id) {
        return;
      }
      await mutateAsyncMembershipSubscriptionStartDate({
        subscriptionId: activeMembershipSubscription.id,
        params:
          !isStartImmediately && date
            ? {
                startSubscriptionEffectiveImmediately: false,
                pendingStartDate:
                  DateTime.fromJSDate(date).toISODate() || undefined,
              }
            : { startSubscriptionEffectiveImmediately: true },
      });

      toast({
        message: t(
          "page.hostedPagesProfileChangeStartDate.toast.startDateChanged",
        ),
        intent: "success",
        type: "small",
      });
      history.replace(createSiteOrEmbedLink(RouteFeature.SiteProfile));
    },
  });

  const disabledDate = useMemo(() => {
    const nowInGym = getDateTimeInGym(timezone);

    return {
      before: nowInGym.add(1, "days").toDate(),
    };
  }, [timezone]);

  return (
    <div
      className={cn("track-height flex w-full flex-col overflow-y-auto", {
        "h-fit": isEmbed,
        "h-full": !isEmbed,
      })}
    >
      <HostedPagesBackHeader
        label={t("page.hostedPagesProfileChangeStartDate.header")}
      />
      <div
        ref={parent}
        className={cn(
          "flex flex-col items-center bg-gray-50 dark:bg-gray-900 p-6",
          {
            "h-fit": isEmbed,
            "h-full": !isEmbed,
          },
        )}
      >
        {isLoadingActiveMembershipSubscription ||
        !activeMembershipSubscription ? (
          <Spinner />
        ) : (
          <div className="flex w-full max-w-lg flex-col gap-10">
            <FormikProvider value={formik}>
              <div className="flex flex-col gap-4">
                <div className="text-lg font-medium text-gray-500 dark:text-gray-400">
                  <Trans i18nKey="page.hostedPagesProfileChangeStartDate.description" />
                </div>
                <FormLabeledSwitch<ChangeStartDateFormValues>
                  name="isStartImmediately"
                  label={t(
                    "page.hostedPagesProfileChangeStartDate.form.isStartImmediately",
                  )}
                />
                {!formik.values.isStartImmediately && (
                  <FormSingleDatePicker<ChangeStartDateFormValues>
                    name="date"
                    disabled={disabledDate}
                  />
                )}
              </div>
              <div className="flex flex-col gap-6">
                <div className="flex gap-4">
                  <Button
                    intent="default"
                    className="w-full"
                    onClick={() => history.goBack()}
                  >
                    {t("portal.common.cancel")}
                  </Button>
                  <Button
                    intent="secondary"
                    className="w-full"
                    onClick={async () => await formik.submitForm()}
                  >
                    {t("portal.common.confirm")}
                  </Button>
                </div>
              </div>
            </FormikProvider>
          </div>
        )}
      </div>
    </div>
  );
};
